/* Maria's theme scripts. No dependencies. */
(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  /* ---------- Money ---------- */
  const formatMoney = (cents) => {
    const format = theme.moneyFormat || '{{amount}}';
    const value = Number(cents) / 100;
    const delimit = (decimals, thousands, decimal) => {
      const [whole, fraction] = value.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    const placeholder = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    let amount;
    switch (placeholder) {
      case 'amount_no_decimals': amount = delimit(0, ',', '.'); break;
      case 'amount_with_comma_separator': amount = delimit(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': amount = delimit(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': amount = delimit(2, "'", '.'); break;
      default: amount = delimit(2, ',', '.');
    }
    // Match Liquid's money_without_trailing_zeros.
    amount = amount.replace(/[.,]00$/, '');
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  };

  /* ---------- Toast ---------- */
  let toastTimer;
  const toast = (message) => {
    const element = qs('[data-toast]');
    if (!element || !message) return;
    element.textContent = message;
    element.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => element.classList.remove('is-visible'), 2800);
  };

  /* ---------- Drawers ---------- */
  const openDrawer = (id) => {
    const dialog = document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (!dialog.open) {
      dialog.classList.remove('is-closing');
      dialog.showModal();
      dialog.dispatchEvent(new CustomEvent('drawer:open'));
    }
    return true;
  };

  const closeDrawer = (dialog) => {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    const finish = () => {
      dialog.classList.remove('is-closing');
      dialog.close();
    };
    if (!dialog.classList.contains('drawer') || reducedMotion()) {
      finish();
      return;
    }
    dialog.classList.add('is-closing');
    setTimeout(finish, 280);
  };

  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target instanceof HTMLDialogElement && event.target.classList.contains('drawer')) {
        event.preventDefault();
        closeDrawer(event.target);
      }
    },
    true
  );

  /* ---------- Cart ---------- */
  const cartSectionIds = () => {
    const ids = ['cart-icon-bubble'];
    if (qs('#CartDrawer')) ids.push('cart-drawer');
    qsa('[data-cart-section]').forEach((element) => ids.push(element.dataset.cartSection));
    return ids;
  };

  const updateCartUI = (sections) => {
    if (!sections) return;
    if (sections['cart-icon-bubble']) {
      const count = parseInt(parseHTML(sections['cart-icon-bubble']).body.textContent.trim(), 10) || 0;
      qsa('[data-cart-count]').forEach((element) => {
        element.textContent = count;
        element.hidden = count === 0;
      });
    }
    if (sections['cart-drawer']) {
      const fresh = qs('[data-cart-drawer-content]', parseHTML(sections['cart-drawer']));
      const current = qs('#CartDrawer [data-cart-drawer-content]');
      if (fresh && current) current.replaceWith(fresh);
    }
    qsa('[data-cart-section]').forEach((element) => {
      const html = sections[element.dataset.cartSection];
      const fresh = html && qs('[data-cart-section]', parseHTML(html));
      if (fresh) element.replaceWith(fresh);
    });
  };

  /* Accepts FormData from product forms, or a JSON payload such as { items: [...] } (bundles). */
  const addToCart = async (payload) => {
    const sections = cartSectionIds().join(',');
    const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    let body = payload;
    if (payload instanceof FormData) {
      payload.append('sections', sections);
      payload.append('sections_url', window.location.pathname);
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify({ ...payload, sections, sections_url: window.location.pathname });
    }
    const response = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers, body });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
    updateCartUI(data.sections);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    if (theme.cartType === 'page') {
      window.location.href = theme.routes.cart;
    } else if (!openDrawer('CartDrawer')) {
      toast(theme.strings.added);
    }
    return data;
  };

  const changeLine = async (key, quantity, item) => {
    if (item) item.classList.add('is-updating');
    try {
      const response = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: cartSectionIds(), sections_url: window.location.pathname })
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      updateCartUI(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    } catch (error) {
      toast(error.message);
      if (item) item.classList.remove('is-updating');
    }
  };

  /* Shared helpers for feature scripts that load on demand (e.g. bundle-offer.js). */
  Object.assign(theme, {
    formatMoney,
    toast,
    openDrawer,
    closeDrawer,
    cart: { add: addToCart, change: changeLine }
  });
  window.theme = theme;

  /* Report checkouts that contain a bundle. Lines carry data-bundle-line (snippets/cart-items.liquid). */
  document.addEventListener('submit', (event) => {
    if (!event.submitter || event.submitter.name !== 'checkout') return;
    const ids = [...new Set(qsa('[data-bundle-line]').map((line) => line.dataset.bundleLine))];
    if (!ids.length) return;
    const detail = { bundle_ids: ids };
    document.dispatchEvent(new CustomEvent('bundle:checkout_started', { detail }));
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('bundle_checkout_started', detail);
      }
    } catch (error) {
      /* Analytics must never block checkout. */
    }
  });

  /* ---------- Wishlist ---------- */
  const WISHLIST_KEY = 'bv-wishlist';
  const wishlist = {
    get() {
      try {
        const list = JSON.parse(localStorage.getItem(WISHLIST_KEY));
        return Array.isArray(list) ? list : [];
      } catch (error) {
        return [];
      }
    },
    set(list) {
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(list.slice(0, 60)));
      } catch (error) {
        /* Storage unavailable: keep the page usable. */
      }
      this.sync();
    },
    toggle(handle) {
      const list = this.get();
      const index = list.indexOf(handle);
      if (index > -1) list.splice(index, 1);
      else list.unshift(handle);
      this.set(list);
      return index === -1;
    },
    sync() {
      const list = this.get();
      qsa('[data-wishlist-toggle]').forEach((button) => {
        button.setAttribute('aria-pressed', String(list.includes(button.dataset.wishlistToggle)));
      });
      qsa('[data-wishlist-count]').forEach((element) => {
        element.textContent = list.length;
        element.hidden = list.length === 0;
      });
    }
  };

  class WishlistItems extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('dialog');
      if (this.drawer) this.drawer.addEventListener('drawer:open', () => this.render());
    }

    async render() {
      const list = wishlist.get();
      const empty = qs('[data-wishlist-empty]', this.drawer);
      const label = qs('[data-wishlist-count-label]', this.drawer);
      if (label) label.textContent = list.length ? `(${list.length})` : '';
      if (empty) empty.hidden = list.length > 0;
      const key = list.join(',');
      if (this.renderedKey === key) return;
      this.renderedKey = key;
      if (!list.length) {
        this.innerHTML = '';
        return;
      }
      const root = (theme.routes.root || '/').replace(/\/$/, '');
      const cards = await Promise.all(
        list.map((handle) =>
          fetch(`${root}/products/${encodeURIComponent(handle)}?view=card`)
            .then((response) => (response.ok ? response.text() : ''))
            .catch(() => '')
        )
      );
      this.innerHTML = cards
        .filter((html) => html.includes('data-product-card'))
        .map((html) => `<div class="wishlist-grid__item">${html}</div>`)
        .join('');
      wishlist.sync();
    }
  }
  customElements.define('wishlist-items', WishlistItems);

  /* ---------- Global clicks ---------- */
  document.addEventListener('click', (event) => {
    const target = event.target;

    const opener = target.closest('[data-drawer-open]');
    if (opener) {
      const id = opener.getAttribute('data-drawer-open');
      const current = opener.closest('dialog[open]');
      if (current && current.id !== id) current.close();
      if (openDrawer(id)) event.preventDefault();
      return;
    }

    const closer = target.closest('[data-drawer-close]');
    if (closer && closer.closest('dialog')) {
      closeDrawer(closer.closest('dialog'));
      return;
    }

    if (target instanceof HTMLDialogElement && target.open) {
      closeDrawer(target);
      return;
    }

    const cartLink = target.closest('[data-cart-open]');
    if (cartLink && theme.cartType === 'drawer' && !document.body.classList.contains('t-cart') && openDrawer('CartDrawer')) {
      event.preventDefault();
      return;
    }

    const searchToggle = target.closest('[data-search-toggle]');
    if (searchToggle) {
      const header = qs('[data-header]');
      const open = !header.classList.contains('is-search-open');
      header.classList.toggle('is-search-open', open);
      searchToggle.setAttribute('aria-expanded', String(open));
      if (open) qs('#HeaderSearchInput').focus();
      return;
    }

    const addButton = target.closest('[data-add-variant]');
    if (addButton) {
      event.preventDefault();
      const formData = new FormData();
      formData.append('id', addButton.dataset.addVariant);
      formData.append('quantity', '1');
      addButton.classList.add('is-loading');
      addToCart(formData)
        .catch((error) => toast(error.message))
        .finally(() => {
          addButton.classList.remove('is-loading');
          const card = addButton.closest('.card');
          if (card) card.classList.remove('is-quick-open');
        });
      return;
    }

    const quickToggle = target.closest('[data-quick-toggle]');
    if (quickToggle) {
      const card = quickToggle.closest('.card');
      const open = !card.classList.contains('is-quick-open');
      qsa('.card.is-quick-open').forEach((other) => other !== card && other.classList.remove('is-quick-open'));
      card.classList.toggle('is-quick-open', open);
      quickToggle.setAttribute('aria-expanded', String(open));
      return;
    }

    const wishButton = target.closest('[data-wishlist-toggle]');
    if (wishButton) {
      event.preventDefault();
      const added = wishlist.toggle(wishButton.dataset.wishlistToggle);
      toast(added ? theme.strings.wishlistAdded : theme.strings.wishlistRemoved);
      const drawerItem = wishButton.closest('.wishlist-grid__item');
      if (!added && drawerItem) {
        const drawerList = drawerItem.closest('wishlist-items');
        drawerItem.remove();
        if (drawerList) drawerList.render();
      }
      return;
    }

    const removeButton = target.closest('[data-cart-remove]');
    if (removeButton) {
      changeLine(removeButton.dataset.cartRemove, 0, removeButton.closest('.cart-item'));
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-qty]');
    if (input) changeLine(input.dataset.cartQty, Math.max(0, parseInt(input.value, 10) || 0), input.closest('.cart-item'));
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !(form.getAttribute('action') || '').includes('/cart/add')) return;
    if (event.submitter && event.submitter.name === 'return_to') return;
    event.preventDefault();
    const button = event.submitter || qs('[data-add-button]', form);
    const errorElement = qs('[data-form-error]', form);
    if (errorElement) errorElement.hidden = true;
    if (button) button.classList.add('is-loading');
    try {
      await addToCart(new FormData(form));
    } catch (error) {
      if (errorElement) {
        errorElement.textContent = error.message;
        errorElement.hidden = false;
      } else {
        toast(error.message);
      }
    } finally {
      if (button) button.classList.remove('is-loading');
    }
  });

  /* Load the second card image only for mouse users, on first hover. */
  document.addEventListener(
    'pointerover',
    (event) => {
      if (event.pointerType !== 'mouse' || !event.target.closest) return;
      const card = event.target.closest('[data-product-card]');
      if (!card || card.dataset.hoverReady) return;
      card.dataset.hoverReady = 'true';
      const image = qs('.card__img--hover[data-srcset]', card);
      if (!image) return;
      image.addEventListener('load', () => image.classList.add('is-loaded'), { once: true });
      image.srcset = image.dataset.srcset;
      image.removeAttribute('data-srcset');
    },
    { passive: true }
  );

  /* ---------- Header ---------- */
  const header = qs('[data-header]');
  if (header) {
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 40);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.items = qsa('.announcement__message', this);
      if (this.items.length < 2 || reducedMotion()) return;
      this.index = 0;
      this.timer = setInterval(() => {
        this.items[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index].classList.add('is-active');
      }, 5000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  class SearchForm extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      if (!this.input || !this.results || this.dataset.predictive !== 'true') return;
      this.cache = new Map();
      this.input.addEventListener('input', debounce(() => this.search(), 260));
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim().length > 1 && this.results.innerHTML.trim()) this.open();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      });
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.close();
        return;
      }
      if (this.cache.has(term)) {
        this.render(this.cache.get(term));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(term)}&resources[type]=product,collection,query&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const section = qs('#shopify-section-predictive-search', parseHTML(await response.text()));
        const markup = section ? section.innerHTML : '';
        this.cache.set(term, markup);
        this.render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      if (markup.trim()) this.open();
      else this.close();
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }
  }
  customElements.define('search-form', SearchForm);

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-qty-step]');
        if (!step || !this.input) return;
        const min = parseInt(this.input.min, 10) || 0;
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const current = parseInt(this.input.value, 10) || 0;
        const next = Math.min(max, Math.max(min, current + Number(step.dataset.qtyStep)));
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Carousels ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-slider-track]', this);
      this.prev = qs('[data-slider-prev]', this);
      this.next = qs('[data-slider-next]', this);
      if (!this.track || !this.prev || !this.next) return;
      this.prev.addEventListener('click', () => this.page(-1));
      this.next.addEventListener('click', () => this.page(1));
      this.track.addEventListener('scroll', () => this.queue(), { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queue());
        this.resizeObserver.observe(this.track);
      }
      this.queue();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    page(direction) {
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.9, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    queue() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const max = this.track.scrollWidth - this.track.clientWidth - 4;
        this.prev.disabled = this.track.scrollLeft <= 4;
        this.next.disabled = this.track.scrollLeft >= max;
      });
    }
  }
  customElements.define('slider-component', SliderComponent);

  class SlideshowComponent extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-slider-track]', this);
      this.slides = qsa('.hero__slide', this);
      this.dots = qsa('[data-slide-to]', this);
      this.index = 0;
      if (!this.track || this.slides.length < 2) return;

      const prev = qs('[data-slide-prev]', this);
      const next = qs('[data-slide-next]', this);
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.slideTo))));
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.sync());
        },
        { passive: true }
      );

      const designMode = window.Shopify && window.Shopify.designMode;
      if (this.dataset.autoplay === 'true' && !reducedMotion() && !designMode) {
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('touchstart', () => {
          this.userPaused = true;
          this.stop();
        }, { passive: true });
        this.observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting && !this.userPaused) this.start();
          else this.stop();
        });
        this.observer.observe(this);
      }

      this.onBlockSelect = (event) => {
        const index = this.slides.findIndex((slide) => slide.contains(event.target));
        if (index > -1) this.go(index, true);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.stop();
      if (this.observer) this.observer.disconnect();
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    go(index, instant) {
      const target = (index + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: instant || reducedMotion() ? 'auto' : 'smooth' });
    }

    sync() {
      const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (index === this.index) return;
      this.index = index;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    start() {
      this.stop();
      this.timer = setInterval(() => {
        if (!document.hidden) this.go(this.index + 1);
      }, Number(this.dataset.speed) || 6000);
    }

    stop() {
      clearInterval(this.timer);
    }
  }
  customElements.define('slideshow-component', SlideshowComponent);

  /* ---------- Product page ---------- */
  const renderPrice = (element, variant) => {
    const current = qs('.price__current', element);
    if (current) current.innerHTML = formatMoney(variant.price);
    const onSale = Boolean(variant.compare_at_price) && variant.compare_at_price > variant.price;
    element.classList.toggle('price--sale', onSale);
    let compare = qs('.price__compare', element);
    let tag = qs('.price__tag', element);
    if (!onSale) {
      if (compare) compare.remove();
      if (tag) tag.remove();
      return;
    }
    if (!compare) {
      compare = document.createElement('s');
      compare.className = 'price__compare';
      element.appendChild(compare);
    }
    compare.innerHTML = `<span class="visually-hidden">${theme.strings.regularPrice || ''}</span>${formatMoney(variant.compare_at_price)}`;
    if (element.classList.contains('price--lg')) {
      if (!tag) {
        tag = document.createElement('span');
        tag.className = 'price__tag';
        element.appendChild(tag);
      }
      const percent = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
      tag.textContent = (theme.strings.percentOff || '__PERCENT__% off').replace('__PERCENT__', percent);
    }
  };

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      try {
        this.variants = JSON.parse(qs('[data-variants]', this).textContent);
      } catch (error) {
        this.variants = [];
      }
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }

    selected() {
      return qsa('fieldset', this).map((fieldset) => {
        const checked = qs('input:checked', fieldset);
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const selected = this.selected();
      qsa('[data-selected-value]', this).forEach((element, index) => {
        element.textContent = selected[index] || '';
      });
      this.updateAvailability();
      const variant = this.variants.find((item) => item.options.every((value, index) => value === selected[index]));
      const product = this.closest('product-info');
      if (product) product.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    }

    updateAvailability() {
      const selected = this.selected();
      qsa('fieldset', this).forEach((fieldset, optionIndex) => {
        qsa('input', fieldset).forEach((input) => {
          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[optionIndex] === input.value &&
              variant.options.every((value, index) => index === optionIndex || value === selected[index])
          );
          if (input.nextElementSibling) input.nextElementSibling.classList.toggle('is-unavailable', !available);
        });
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.addEventListener('variant:change', (event) => this.onVariant(event.detail.variant));
      this.initStickyBar();
    }

    onVariant(variant) {
      const buttons = qsa('[data-add-button], [data-buy-button]', this);
      const labels = qsa('[data-add-label]', this);
      const stock = qs('[data-stock]', this);
      const stockLabel = qs('[data-stock-label]', this);

      if (!variant) {
        buttons.forEach((button) => { button.disabled = true; });
        labels.forEach((label) => { label.textContent = theme.strings.unavailable; });
        if (stock) stock.classList.add('stock--out');
        if (stockLabel) stockLabel.textContent = theme.strings.unavailable;
        return;
      }

      const input = qs('[data-variant-input]', this);
      if (input) input.value = variant.id;
      buttons.forEach((button) => { button.disabled = !variant.available; });
      labels.forEach((label) => {
        label.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      });
      if (stock) stock.classList.toggle('stock--out', !variant.available);
      if (stockLabel) stockLabel.textContent = variant.available ? theme.strings.inStock : theme.strings.soldOut;
      qsa('[data-price]', this).forEach((price) => renderPrice(price, variant));

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      const gallery = qs('media-gallery', this);
      if (gallery && variant.featured_media) gallery.show(variant.featured_media.id);
    }

    initStickyBar() {
      const bar = qs('[data-sticky-atc]', this);
      const target = qs('[data-buy-box]', this);
      if (!bar || !target || !('IntersectionObserver' in window)) return;
      const button = qs('button', bar);
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        if (button) button.tabIndex = show ? 0 : -1;
      }).observe(target);
    }
  }
  customElements.define('product-info', ProductInfo);

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = qs('[data-gallery-viewport]', this);
      this.slides = qsa('.gallery__slide', this);
      this.thumbs = qsa('[data-thumb]', this);
      this.dots = qsa('.gallery__dot', this);
      if (!this.viewport) return;
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      if (this.activeIndex > 0) this.viewport.scrollLeft = this.activeIndex * this.viewport.clientWidth;

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.show(thumb.dataset.thumb)));
      this.viewport.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.setActive(Math.round(this.viewport.scrollLeft / this.viewport.clientWidth)));
        },
        { passive: true }
      );
      this.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-zoom-src]');
        if (zoom && !zoom.disabled) {
          this.openZoom(zoom.dataset.zoomSrc);
          return;
        }
        if (event.target.closest('[data-zoom-stage]')) {
          const dialog = qs('[data-zoom-dialog]', this);
          if (dialog) dialog.close();
        }
      });
    }

    show(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      this.viewport.scrollTo({ left: index * this.viewport.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
      this.setActive(index);
    }

    setActive(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }

    openZoom(src) {
      const dialog = qs('[data-zoom-dialog]', this);
      const stage = qs('[data-zoom-stage]', this);
      if (!dialog || !stage) return;
      const image = new Image();
      image.alt = '';
      image.src = src;
      stage.replaceChildren(image);
      dialog.showModal();
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class ShareButton extends HTMLElement {
    connectedCallback() {
      const button = qs('button', this);
      if (!button) return;
      button.addEventListener('click', async () => {
        const data = { title: this.dataset.title, url: this.dataset.url };
        if (navigator.share) {
          try {
            await navigator.share(data);
          } catch (error) {
            /* Share sheet dismissed. */
          }
          return;
        }
        try {
          await navigator.clipboard.writeText(data.url);
          toast(theme.strings.linkCopied);
        } catch (error) {
          window.prompt('', data.url);
        }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          fetch(this.dataset.url)
            .then((response) => response.text())
            .then((html) => {
              const fresh = qs('product-recommendations', parseHTML(html));
              if (fresh && fresh.children.length) {
                this.innerHTML = fresh.innerHTML;
                wishlist.sync();
              } else {
                this.hidden = true;
              }
            })
            .catch(() => {
              this.hidden = true;
            });
        },
        { rootMargin: '0px 0px 400px 0px' }
      ).observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Collection filters ---------- */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-sort-select]')) {
          this.setSort(event.target.value);
        } else if (event.target.type === 'checkbox' && event.target.closest('[data-facets-form]')) {
          this.submit();
        }
      });
      this.addEventListener('submit', (event) => {
        if (!event.target.matches('[data-facets-form]')) return;
        event.preventDefault();
        this.submit();
      });
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.togglePanel(false);
      });
      this.onPopState = () => this.load(window.location.href, false);
      window.addEventListener('popstate', this.onPopState);
      this.applyDensity();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    onClick(event) {
      const target = event.target;
      const sort = target.closest('[data-sort-value]');
      if (sort) {
        this.setSort(sort.dataset.sortValue);
        return;
      }
      const link = target.closest('[data-facet-link]');
      if (link) {
        event.preventDefault();
        this.load(link.href);
        return;
      }
      const density = target.closest('[data-density]');
      if (density) {
        this.setDensity(density.dataset.density);
        return;
      }
      if (target.closest('[data-filters-open]')) {
        this.togglePanel(true);
        return;
      }
      if (target.closest('[data-filters-close]')) {
        this.togglePanel(false);
        return;
      }
      const more = target.closest('[data-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
      }
    }

    get form() {
      return qs('[data-facets-form]', this);
    }

    setSort(value) {
      const form = this.form;
      const input = form && qs('[data-sort-input]', form);
      if (input) {
        input.value = value;
        this.submit();
        return;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');
      this.load(url.toString());
    }

    submit() {
      const form = this.form;
      if (!form) return;
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      this.load(`${form.getAttribute('action')}?${params.toString()}`);
    }

    async load(url, push = true) {
      const results = qs('[data-results]', this);
      if (results) results.classList.add('is-loading');
      try {
        const fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set('section_id', this.sectionId);
        const response = await fetch(fetchUrl.toString());
        if (!response.ok) throw new Error(String(response.status));
        const fresh = qs('facet-filters', parseHTML(await response.text()));
        if (!fresh) throw new Error('Missing results');
        const freshResults = qs('[data-results]', fresh);
        if (results && freshResults) results.replaceWith(freshResults);
        const panel = qs('[data-facets-panel]', this);
        const freshPanel = qs('[data-facets-panel]', fresh);
        if (panel && freshPanel) panel.innerHTML = freshPanel.innerHTML;
        if (push) window.history.pushState({}, '', url);
        this.applyDensity();
        wishlist.sync();
        const top = this.getBoundingClientRect().top;
        if (top < 0 && !(panel && panel.classList.contains('is-open'))) {
          window.scrollTo({ top: window.scrollY + top - 150, behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
      } catch (error) {
        window.location.href = url;
      }
    }

    async loadMore(button) {
      button.classList.add('is-loading');
      try {
        const fetchUrl = new URL(button.href, window.location.origin);
        fetchUrl.searchParams.set('section_id', this.sectionId);
        const response = await fetch(fetchUrl.toString());
        if (!response.ok) throw new Error(String(response.status));
        const doc = parseHTML(await response.text());
        const grid = qs('[data-product-grid]', this);
        qsa('[data-product-grid] > .product-grid__item', doc).forEach((item) => grid.appendChild(document.adoptNode(item)));
        const wrap = qs('[data-load-more-wrap]', this);
        const freshWrap = qs('[data-load-more-wrap]', doc);
        if (freshWrap) wrap.replaceWith(document.adoptNode(freshWrap));
        else wrap.remove();
        wishlist.sync();
      } catch (error) {
        window.location.href = button.href;
      }
    }

    setDensity(value) {
      const key = window.matchMedia('(max-width: 749px)').matches ? 'bv-density-mobile' : 'bv-density';
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        /* Storage unavailable. */
      }
      this.applyDensity(value);
    }

    applyDensity(forced) {
      const grid = qs('[data-product-grid]', this);
      if (!grid) return;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      let desktop = null;
      let phone = null;
      try {
        desktop = localStorage.getItem('bv-density');
        phone = localStorage.getItem('bv-density-mobile');
      } catch (error) {
        /* Storage unavailable. */
      }
      if (forced) {
        if (mobile) phone = forced;
        else desktop = forced;
      }
      if (desktop) grid.style.setProperty('--cols', desktop);
      if (phone) grid.style.setProperty('--cols-mobile', phone);
      const active = mobile ? phone || '2' : desktop || grid.style.getPropertyValue('--cols').trim();
      qsa('[data-density]', this).forEach((button) => {
        const on = button.dataset.density === active;
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-pressed', String(on));
      });
    }

    togglePanel(open) {
      const panel = qs('[data-facets-panel]', this);
      if (!panel) return;
      panel.classList.toggle('is-open', open);
      document.documentElement.style.overflow = open && window.matchMedia('(max-width: 989px)').matches ? 'hidden' : '';
      if (open) {
        const focusable = qs('button, input, a', panel);
        if (focusable) focusable.focus();
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ---------- Recently viewed ---------- */
  /* History is kept in the visitor's own browser only. Every read and write
     is guarded: private-browsing modes can throw on localStorage access, and
     the section must degrade to simply not appearing rather than breaking. */
  const RECENT_KEY = 'marias:recently-viewed';
  const RECENT_MAX = 12;

  const readRecent = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(RECENT_KEY));
      return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string') : [];
    } catch (e) {
      return [];
    }
  };

  const rememberProduct = (handle) => {
    if (!handle) return;
    try {
      const list = readRecent().filter((h) => h !== handle);
      list.unshift(handle);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {
      /* Nothing to do: the visit just is not remembered. */
    }
  };

  const currentProductHandle = () => {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  };

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      const current = this.dataset.current || currentProductHandle();
      const handles = readRecent().filter((handle) => handle && handle !== current);
      if (!handles.length || !('IntersectionObserver' in window)) return;

      const limit = parseInt(this.dataset.limit, 10) || 6;
      const wanted = handles.slice(0, limit);

      new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          this.render(wanted);
        },
        { rootMargin: '0px 0px 600px 0px' }
      ).observe(this);
    }

    render(handles) {
      const track = qs('[data-recent-track]', this);
      if (!track) return;
      const root = ((theme.routes && theme.routes.root) || '/').replace(/\/$/, '');

      Promise.all(
        handles.map((handle) =>
          fetch(`${root}/products/${encodeURIComponent(handle)}?view=card`)
            .then((response) => (response.ok ? response.text() : ''))
            .catch(() => '')
        )
      ).then((cards) => {
        /* A handle can 404 if the product was unpublished since the visit. */
        const html = cards
          .filter((card) => card && card.trim())
          .map((card) => `<li class="product-grid__item">${card}</li>`)
          .join('');
        if (!html) return;
        track.innerHTML = html;
        this.hidden = false;
        if (wishlist && typeof wishlist.sync === 'function') wishlist.sync();
      });
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* Recorded on every product page, whether or not the section is present,
     so the history is already warm the first time the section renders. */
  rememberProduct(currentProductHandle());

  wishlist.sync();
})();
